import { AlertCircleIcon, Key01Icon } from '@langchain/untitled-ui-icons';

import { ReactNode, useCallback, useMemo, useState } from 'react';

import { Button, IconButton, Spinner, Text } from '@/design-system';
import { useHasWorkspaceSecrets } from '@/hooks/useHasWorkspaceSecrets';
import { useRunsQuery } from '@/hooks/useSwr';
import { cn } from '@/utils/tailwind';

import { SplitViewPane } from '../../components/SplitViewPane';
import { EvaluatorSecrets } from '../Settings/components/EvaluatorSecrets';
import { AttributesSection } from './components/AttributesSection';
import { CategoriesSection } from './components/CategoriesSection';
import { SavedConfigs } from './components/SavedConfigurations/SavedConfigs';
import { useInsightsJobConfigs } from './components/SavedConfigurations/hooks/useInsightsJobConfigs';
import { SelectRunsSection } from './components/SelectRunsSection';
import { SummaryPromptSection } from './components/SummaryPromptSection';
import {
  CONFIG_SECTIONS,
  CONFIG_SETTINGS_SECTIONS,
  ConfigSection,
  DEFAULT_CONFIG_ID,
  REQUIRED_API_KEYS,
} from './constants';
import { useSelectedConfig } from './hooks/useSelectedConfig';
import { useSubmitForm } from './hooks/useSubmitForm';
import {
  GetInsightsJobConfigResponse,
  InsightsJobConfigWithId,
  InsightsJobFormData,
} from './types';
import { addIds } from './utils/formatUtils';
import { getDefaultFormData } from './utils/getDefaultFormData';

const ConfigSectionButton = ({
  section,
  activeSection,
  setActiveSection,
  hasErrors,
  index,
}: {
  section: ConfigSection;
  activeSection: ConfigSection;
  setActiveSection: () => void;
  hasErrors: boolean;
  index: number;
}) => {
  const active = activeSection === section;
  return (
    <Button
      variant={active ? 'normal' : 'plain'}
      color="secondary"
      size="md"
      onClick={setActiveSection}
      className={cn(
        'justify-start gap-4 border-none p-4',
        active && 'bg-tertiary hover:bg-tertiary'
      )}
    >
      <Text variant="h3" weight="medium" className="text-quaternary">
        {index + 1}
      </Text>
      <Text variant="h3" weight="medium">
        {section}
      </Text>
      {hasErrors && <AlertCircleIcon className="h-4 w-4 text-error" />}
    </Button>
  );
};

export const InsightsJobConfig = ({
  isOpen,
  sessionId,
  onSuccess,
  onClose,
}: {
  isOpen: boolean;
  sessionId: string;
  onSuccess: () => void;
  onClose: () => void;
}) => {
  const defaultFormData = getDefaultFormData();
  const [formData, setFormData] =
    useState<InsightsJobFormData>(defaultFormData);

  const [errors, setErrors] = useState<
    Partial<Record<keyof InsightsJobFormData, string>>
  >({});

  const [activeSection, setActiveSection] = useState<ConfigSection>(
    ConfigSection.SELECT_RUNS
  );

  const { openSecretsRef, missingSecrets } =
    useHasWorkspaceSecrets(REQUIRED_API_KEYS);

  const updateFormData = useCallback(
    (updates: Partial<InsightsJobFormData>) => {
      setFormData((prev) => {
        const newData = { ...prev, ...updates };

        // Clear related errors when other fields change
        setErrors((prev) => {
          const newErrors = { ...prev };
          Object.keys(updates).forEach((key) => {
            delete newErrors[key as keyof InsightsJobFormData];
          });
          return newErrors;
        });

        return newData;
      });
    },
    []
  );

  const { data: runs } = useRunsQuery(
    {
      session: sessionId ? [sessionId] : undefined,
      limit: 1,
      filter: formData.filter.filter,
      select: ['inputs', 'outputs'],
    },
    {},
    { revalidateOnFocus: false }
  );

  const { selectedConfigId, setSelectedConfig } = useSelectedConfig({
    updateFormData,
  });

  const [hasInitialized, setHasInitialized] = useState(false);

  const { data, isLoading } = useInsightsJobConfigs({
    sessionId,
    includePrebuilts: true,
    options: {
      onSuccess: (data: GetInsightsJobConfigResponse) => {
        if (!hasInitialized) {
          const configsList = addIds(data.configs);
          setSelectedConfig({ id: DEFAULT_CONFIG_ID, configsList });
          setHasInitialized(true);
        }
      },
      onError: () => {
        updateFormData(getDefaultFormData());
      },
    },
  });

  const configsList: InsightsJobConfigWithId[] = useMemo(() => {
    return addIds(data?.configs || []);
  }, [data?.configs]);

  const resetForm = useCallback(
    (configId: string | null) => {
      setFormData(getDefaultFormData());
      setErrors({});
      setActiveSection(ConfigSection.SELECT_RUNS);
      setSelectedConfig({ id: configId, configsList });
    },
    [setSelectedConfig, configsList]
  );

  // Only update the config if it is not a prebuilt config
  const selectedConfigIdToUpdate = useMemo(() => {
    return selectedConfigId &&
      !configsList.find((config) => config.id === selectedConfigId)?.prebuilt
      ? selectedConfigId
      : undefined;
  }, [selectedConfigId, configsList]);

  const { submitForm, isSubmitting } = useSubmitForm({
    sessionId,
    formData,
    setErrors,
    onSuccess,
    onClose,
    resetForm: () => resetForm(DEFAULT_CONFIG_ID),
    openSecretsRef,
    missingSecrets,
    selectedConfigId: selectedConfigIdToUpdate,
  });

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      resetForm(DEFAULT_CONFIG_ID);
      setHasInitialized(false);
    }
  }, [isSubmitting, onClose, resetForm]);

  const sectionComponentMap = useMemo(
    () => ({
      [ConfigSection.SELECT_RUNS]: (
        <SelectRunsSection
          formData={formData}
          updateFormData={updateFormData}
          errors={errors}
          sessionId={sessionId}
        />
      ),
      [ConfigSection.CATEGORIES]: (
        <CategoriesSection
          formData={formData}
          updateFormData={updateFormData}
          errors={errors}
        />
      ),
      [ConfigSection.SUMMARY_PROMPT]: (
        <SummaryPromptSection
          formData={formData}
          updateFormData={updateFormData}
          latestRun={runs?.runs?.[0]}
          errors={errors}
          configKey={selectedConfigId}
        />
      ),
      [ConfigSection.ATTRIBUTES]: (
        <AttributesSection
          formData={formData}
          updateFormData={updateFormData}
          errors={errors}
        />
      ),
    }),
    [formData, updateFormData, errors, sessionId, selectedConfigId, runs?.runs]
  );

  const sectionsWithErrors: Set<ConfigSection> = useMemo(() => {
    return new Set(
      Object.keys(errors).map((key) => CONFIG_SETTINGS_SECTIONS[key])
    );
  }, [errors]);

  const renderHeader = (): ReactNode => {
    return (
      <div className="grid w-full grid-cols-[2fr,5fr]">
        {!isLoading && configsList?.length ? (
          <div className="my-auto min-w-0 px-4">
            <SavedConfigs
              formData={formData}
              selectedConfigId={selectedConfigId}
              configs={configsList}
              setSelectedConfig={({ id, skipContentUpdate }) =>
                setSelectedConfig({ id, configsList, skipContentUpdate })
              }
              setErrors={setErrors}
              resetForm={() => resetForm(null)}
            />
          </div>
        ) : isLoading ? (
          <Spinner />
        ) : null}
        <div className="flex items-center justify-between border-b border-l border-secondary p-2">
          <div className="ml-3">
            <Text variant="h2" weight="medium">
              New Insights
            </Text>
          </div>
          <div className="flex flex-row gap-2">
            <EvaluatorSecrets
              apiKeys={REQUIRED_API_KEYS}
              infoMessage="Updating your workspace API keys will update the API key for all members of the workspace."
              triggerComponent={
                <IconButton
                  icon={Key01Icon}
                  label="API Key"
                  variant="plain"
                  size="md"
                  color="secondary"
                />
              }
              openSecretsRef={openSecretsRef}
            />
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClose}
              size="md"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={submitForm}
              loading={isSubmitting}
              size="md"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SplitViewPane
      open={isOpen}
      customHeader={renderHeader()}
      className="flex h-full flex-col"
    >
      {isLoading ? (
        <div className="m-4">
          <Spinner />
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-[2fr,5fr]">
          <div className="flex min-h-0 flex-col gap-2 px-3 pt-2">
            {CONFIG_SECTIONS.map((section, index) => (
              <ConfigSectionButton
                key={section}
                section={section as ConfigSection}
                activeSection={activeSection}
                setActiveSection={() =>
                  setActiveSection(section as ConfigSection)
                }
                hasErrors={sectionsWithErrors.has(section)}
                index={index}
              />
            ))}
          </div>
          <div className="flex min-h-0 flex-col gap-6 border-l border-secondary p-5">
            {sectionComponentMap[activeSection]}
          </div>
        </div>
      )}
    </SplitViewPane>
  );
};
